import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Award, Handshake, Leaf, ArrowRight, Quote } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { PageHeader } from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES } from "@/lib/company";

const valueIcons = [ShieldCheck, Award, Handshake, Leaf];

export default function About() {
  const { t } = useLanguage();

  return (
    <div data-testid="about-page">
      <PageHeader overline={t.about.overline} title={t.about.title} subtitle={t.about.lead} />

      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-2 lg:gap-16 lg:px-10 lg:py-28">
          <Reveal>
            <div className="aspect-[4/3] overflow-hidden border border-[#E5E5E5]">
              <img src={IMAGES.team} alt="Elvocom AG Team" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="text-base leading-relaxed text-[#4A4A4A] md:text-lg">{t.about.body1}</p>
            <p className="mt-6 text-base leading-relaxed text-[#4A4A4A] md:text-lg">{t.about.body2}</p>
            <div className="mt-8 border-l-2 border-[#E8620E] pl-6">
              <Quote size={24} className="text-[#E8620E]" />
              <p className="mt-3 text-lg font-medium text-[#0A0A0A]">{t.about.mission}</p>
              <p className="mt-4 text-sm text-[#757575]">
                <span className="font-semibold text-[#0A0A0A]">{t.about.founder}</span>
                {" — "}
                {t.about.founderLabel}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-[#E5E5E5] bg-[#FAFAFA]">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <Reveal>
            <Overline>{t.valuesSection.overline}</Overline>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-[#0A0A0A] md:text-4xl">
              {t.valuesSection.title}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px border border-[#E5E5E5] bg-[#E5E5E5] sm:grid-cols-2 lg:grid-cols-4">
            {t.values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="h-full bg-white p-8">
                    <Icon size={28} className="text-[#E8620E]" strokeWidth={1.5} />
                    <h3 className="mt-5 text-lg font-medium text-[#0A0A0A]">{v.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#4A4A4A]">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#0A0A0A]">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-16 lg:flex-row lg:items-center lg:px-10 lg:py-20">
          <h2 className="max-w-2xl text-3xl font-semibold tracking-tight text-white md:text-4xl">
            {t.ctaBand.title}
          </h2>
          <Link
            to="/kontakt"
            data-testid="about-cta-button"
            className="group flex shrink-0 items-center gap-2 bg-[#E8620E] px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#C4520C]"
          >
            {t.ctaBand.button}
            <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
